import { useCallback, useState } from 'react';

import { useBoundStore } from 'src/store/store';
import type { ForecastDataPoint } from 'src/types/domain/ForecastDataPoint';

export function useForecastPaginatedControls(
  sessionId: string,
  sku: string,
  data: ForecastDataPoint[] = [],
  queryData?: ForecastDataPoint[],
): {
  trendAdjustment: number;
  setTrendAdjustment: (value: number) => void;
  proposalAdjustment: number;
  setProposalAdjustment: (value: number) => void;
  handleIncrease: () => void;
  handleDecrease: () => void;
  handleTrendUp: () => void;
  handleTrendDown: () => void;
  handleReset: () => void;
} {
  const [trendAdjustment, setTrendAdjustment] = useState(5);
  const [proposalAdjustment, setProposalAdjustment] = useState(5);
  const getUpdateForecastProposal = useCallback(() => {
    return useBoundStore.getState().updateForecastProposal;
  }, []);
  const getClearForecastData = useCallback(() => {
    return useBoundStore.getState().clearForecastData;
  }, []);
  const getSetForecastData = useCallback(() => {
    return useBoundStore.getState().setForecastData;
  }, []);
  const handleUpdateForecastProposal = useCallback(
    (adjustment: number) => {
      if (data.length === 0) {
        return;
      }

      const updateForecastProposal = getUpdateForecastProposal();
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      data.forEach((item: ForecastDataPoint) => {
        const isCurrentMonth =
          item.year === currentYear && item.month === currentMonth;

        if (
          (isCurrentMonth || !item.isHistory) &&
          item.forecastProposal !== undefined
        ) {
          const newValue =
            Math.round(Math.max(0, item.forecastProposal + adjustment) * 100) /
            100;
          updateForecastProposal(sku, item.month_id, newValue, sessionId);
        }
      });
    },
    [data, sessionId, sku, getUpdateForecastProposal],
  );

  const handleUpdateForecastTrend = useCallback(
    (direction: 'up' | 'down') => {
      if (data.length === 0) {
        return;
      }

      const updateForecastProposal = getUpdateForecastProposal();

      data.forEach((item: ForecastDataPoint, index: number) => {
        if (!item.isHistory && item.forecastProposal !== undefined) {
          const forecastIndex = index - 24;
          if (forecastIndex >= 0 && forecastIndex < 12) {
            const i = direction === 'up' ? 11 - forecastIndex : forecastIndex;
            const increment =
              direction === 'up'
                ? trendAdjustment - i * (trendAdjustment / 12)
                : trendAdjustment - (12 - i) * (trendAdjustment / 12);
            const newValue =
              direction === 'up'
                ? Math.round(
                    Math.max(0, item.forecastProposal + increment) * 100,
                  ) / 100
                : Math.round(
                    Math.max(0, item.forecastProposal - increment) * 100,
                  ) / 100;

            updateForecastProposal(sku, item.month_id, newValue, sessionId);
          }
        }
      });
    },
    [data, sessionId, trendAdjustment, sku, getUpdateForecastProposal],
  );

  const handleIncrease = useCallback(() => {
    handleUpdateForecastProposal(proposalAdjustment);
  }, [handleUpdateForecastProposal, proposalAdjustment]);

  const handleDecrease = useCallback(() => {
    handleUpdateForecastProposal(-proposalAdjustment);
  }, [handleUpdateForecastProposal, proposalAdjustment]);

  const handleTrendUp = useCallback(() => {
    handleUpdateForecastTrend('up');
  }, [handleUpdateForecastTrend]);

  const handleTrendDown = useCallback(() => {
    handleUpdateForecastTrend('down');
  }, [handleUpdateForecastTrend]);

  const handleReset = useCallback(() => {
    const clearForecastData = getClearForecastData();
    const setForecastData = getSetForecastData();
    clearForecastData(sku, sessionId);
    setForecastData(sku, queryData || [], sessionId);
  }, [sessionId, sku, queryData, getClearForecastData, getSetForecastData]);

  return {
    trendAdjustment,
    setTrendAdjustment,
    proposalAdjustment,
    setProposalAdjustment,
    handleIncrease,
    handleDecrease,
    handleTrendUp,
    handleTrendDown,
    handleReset,
  };
}
